import React, { useRef, useEffect } from 'react';
import { Search, Filter, X } from 'lucide-react';
import FilterPanel from './FilterPanel';
import { TaskStatus } from '../types';

interface SearchFilterBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  // Filter props
  statusFilter: TaskStatus[];
  onStatusToggle: (status: TaskStatus) => void;
  horizonFilter: string[];
  onHorizonToggle: (horizon: string) => void;
  tagsFilter: string[];
  onTagToggle: (tag: string) => void;
  taskTypeFilter: 'all' | 'parent_only' | 'child_only';
  onTaskTypeChange: (type: 'all' | 'parent_only' | 'child_only') => void;
  allTags: string[];
  onClearFilters: () => void;
}

export const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
  searchQuery,
  onSearchChange,
  showFilters,
  onToggleFilters,
  statusFilter,
  onStatusToggle,
  horizonFilter,
  onHorizonToggle,
  tagsFilter,
  onTagToggle,
  taskTypeFilter,
  onTaskTypeChange,
  allTags,
  onClearFilters,
}) => {
  const filterPanelRef = useRef<HTMLDivElement>(null);

  // Handle click outside filter panel to close it
  useEffect(() => {
    if (!showFilters) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target as Node)) {
        onToggleFilters();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilters, onToggleFilters]);

  return (
    <div className="space-y-4">
      {/* Search Bar - Full width on mobile */}
      <div className="flex items-center space-x-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter Toggle - Always visible */}
        <div className="relative" ref={filterPanelRef}>
          <button
            onClick={onToggleFilters}
            className={`flex items-center px-3 py-2 border rounded-lg transition-colors ${
              showFilters
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <Filter className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Filters</span>
          </button>

          {showFilters && (
            <div className="absolute top-full right-0 mt-2 z-50 w-screen max-w-sm sm:w-[480px] sm:max-w-[600px] sm:right-0">
              <FilterPanel
                statusFilter={statusFilter}
                onStatusToggle={onStatusToggle}
                horizonFilter={horizonFilter}
                onHorizonToggle={onHorizonToggle}
                tagsFilter={tagsFilter}
                onTagToggle={onTagToggle}
                taskTypeFilter={taskTypeFilter}
                onTaskTypeChange={onTaskTypeChange}
                availableTags={allTags}
                onClearFilters={onClearFilters}
                onClose={onToggleFilters}
              />
            </div>
          )}
        </div>
      </div>

    </div>
  );
}; 